/**
 * One line of copy an admin authored on the server, rendered in the reader's language where we
 * have it.
 *
 * The server sends the words AND, when the phrase is one clients own, its name — it cannot know
 * that this app files benefit phrases under `benefits.` and club phrases under `club.` while the
 * mobile app files them elsewhere, so the namespace is added by the caller.
 */
export interface ResolvedText {
  /** Ready to render, in every case. */
  text: string
  /** Namespace-free i18n key; empty when the copy is an admin's own words. */
  i18nKey?: string
  /** Values to interpolate into our own translation. */
  args?: Record<string, string>
}

/**
 * The slice of i18next this needs: look a phrase up, and say whether we have it.
 *
 * Narrowed to two methods so the rule is stated in one place and reads without a provider. The
 * `i18n` object from `useTranslation()` satisfies it.
 */
export interface Localizer {
  t: (key: string, args?: Record<string, string>) => string
  exists: (key: string) => boolean
}

/**
 * Render [line] under [namespace]. Three cases, in order:
 * 1. **No key** — an admin's own words, which exist in one language only. Show them.
 * 2. **A key we ship** — show OUR translation, interpolated with the server's values, so the page
 *    stays translated into every language it already supported.
 * 3. **A key we do not ship** — a newer server, or a phrase added after this deploy. Show the
 *    server's own wording, which is always sent for exactly this reason.
 *
 * Case 3 is why `text` is never optional on the wire: a client that went blank on an unrecognized
 * key would break every time somebody added a line.
 */
export const authoredText = (
  localizer: Localizer,
  line: ResolvedText | null | undefined,
  namespace: string
): string => {
  if (!line) return ''
  if (!line.i18nKey) return line.text

  const key = `${namespace}${line.i18nKey}`
  // `exists` rather than comparing `t(key)` to the key: i18next falls back to English before it
  // gives up, and a phrase that legitimately equals its own key would then look missing.
  if (!localizer.exists(key)) return line.text
  return localizer.t(key, line.args ?? {})
}
